import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import type {
  TenantSelectionResult,
  TenantSelectorRowData,
} from '../../../components/tenant-selector/types';
import { useTenantSelectorRowData } from '../../../components/tenant-selector/useTenantSelectorRowData';
import { validateTenantSegments } from '../engage-automations-tab/template-api';

import type {
  TenantSegmentConflict,
  TenantSelectionType,
} from '../engage-automations-tab/templates.types';
import { templateKeys } from '../template-query-keys';
import { useAutomationStatuses, useTemplateSyncStatusPolling } from '../useAutomationTemplates';
import {
  automationStatusesToIndicator,
  automationStatusToTenantLabel,
  SYNCED_AND_ON_LABEL,
} from './automationTemplateTenantStatus';

export function useAutomationTemplateTenantSelection(options: {
  templateId: string;
  tenantSelectionType: TenantSelectionType;
  selectedTenantIds: string[];
}) {
  const { templateId, tenantSelectionType, selectedTenantIds } = options;
  const queryClient = useQueryClient();

  const [rawConflicts, setRawConflicts] = useState<TenantSegmentConflict[]>([]);
  const [isValidating, setIsValidating] = useState(false);

  const { data: templateSyncData, syncingTenantIds } = useTemplateSyncStatusPolling(templateId);

  const jobSyncActive = Boolean(templateSyncData?.hasActiveJobs);

  const { data: automationStatuses, isLoading: isLoadingStatuses } = useAutomationStatuses(templateId, {
    refetchInterval: jobSyncActive ? 3000 : false,
  });

  // Include templateId so switching templates does not compare against stale scope from another template.
  const syncStateKey = `${templateId}:${jobSyncActive}:${syncingTenantIds.slice().sort().join(',')}`;
  const [previousSyncStateKey, setPreviousSyncStateKey] = useState(syncStateKey);

  useEffect(() => {
    if (previousSyncStateKey === syncStateKey) {
      return;
    }
    if (previousSyncStateKey !== '') {
      void queryClient.invalidateQueries({
        queryKey: templateKeys.automationStatuses(templateId),
      });
    }
    setPreviousSyncStateKey(syncStateKey);
  }, [previousSyncStateKey, queryClient, syncStateKey, templateId]);

  const selectedTenantIdsKey = [...selectedTenantIds].sort().join(',');
  const selectedTenantIdsRef = useRef(selectedTenantIds);
  selectedTenantIdsRef.current = selectedTenantIds;

  useEffect(() => {
    const ids = selectedTenantIdsRef.current;
    if (tenantSelectionType !== 'selected' || ids.length === 0) {
      setRawConflicts([]);
      setIsValidating(false);
      return;
    }

    setIsValidating(true);

    let cancelled = false;
    const validate = async () => {
      try {
        const result = await validateTenantSegments(templateId, selectedTenantIdsRef.current);
        if (!cancelled) {
          setRawConflicts(result.conflicts);
        }
      } catch {
        if (!cancelled) {
          setRawConflicts([]);
        }
      } finally {
        if (!cancelled) {
          setIsValidating(false);
        }
      }
    };

    const debounce = setTimeout(validate, 500);
    return () => {
      cancelled = true;
      clearTimeout(debounce);
    };
  }, [templateId, tenantSelectionType, selectedTenantIdsKey]);

  const rowDataByTenantId = useMemo(() => {
    const syncingSet = new Set(syncingTenantIds);
    const map: Record<string, Partial<TenantSelectorRowData>> = {};

    if (!isLoadingStatuses && automationStatuses?.businessStatuses) {
      for (const row of automationStatuses.businessStatuses) {
        const label =
          row.automations.length === 0
            ? 'Not synced'
            : [...new Set(row.automations.map(a => automationStatusToTenantLabel(a.status)))]
                .sort()
                .join(', ');
        const hasAutomationOn = label.includes(SYNCED_AND_ON_LABEL);
        map[row.businessId] = {
          statusIndicator: {
            label,
            indicator: automationStatusesToIndicator(row.automations.map(a => a.status)),
            lockReason: hasAutomationOn
              ? 'This tenant has an active automation. Disable the automation for this tenant before removing it.'
              : undefined,
          },
          isActive: hasAutomationOn,
          isSyncing: syncingSet.has(row.businessId),
        };
      }
    }

    for (const id of syncingTenantIds) {
      if (map[id] == null) {
        map[id] = { isSyncing: true };
      }
    }

    return map;
  }, [isLoadingStatuses, automationStatuses, syncingTenantIds]);

  const { tenantRowData, isLoading } = useTenantSelectorRowData(rowDataByTenantId);

  const mappedConflicts = rawConflicts.map(c => ({
    tenantId: c.tenantId,
    tenantName: c.tenantName,
    description: `will receive: ${c.missingSegments.map(s => s.name).join(', ')}`,
  }));

  return {
    tenantRowData,
    isLoading,
    conflicts:
      mappedConflicts.length > 0
        ? {
            notice: {
              title: 'Tenant segment notice',
              description:
                'This automation uses segments that are not yet enabled for some selected tenants. When you save and sync, those segments will be automatically added to those tenants so the automation can run correctly.',
            },
            items: mappedConflicts,
          }
        : null,
    isValidating,
    syncingTenantCount: syncingTenantIds.length,
    jobSyncActive,
  } satisfies TenantSelectionResult;
}
